import React, { useState, useEffect } from 'react';
import { Briefcase, MapPin, Clock, DollarSign, ChevronRight, X, UploadCloud, CheckCircle, Search } from 'lucide-react';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../utils/api';

// Premium background element
const BackgroundDecoration = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-32 left-1/2 w-96 h-96 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
    </div>
);

// Job openings are now fetched from backend

const Careers = () => {
    const [jobOpenings, setJobOpenings] = useState<any[]>([]);
    const [selectedJob, setSelectedJob] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDept, setFilterDept] = useState('All');

    // Form state
    const backendUrl = API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL;
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        coverLetter: ''
    });
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "Careers | devNectar";
        
        const fetchJobs = async () => {
            try {
                const response = await fetch(`${backendUrl}/api/jobs`);
                if (response.ok) {
                    const data = await response.json();
                    setJobOpenings(data);
                }
            } catch (error) {
                console.error('Failed to fetch jobs:', error);
            }
        };
        fetchJobs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openApplyModal = (job: any) => {
        setSelectedJob(job);
        setIsModalOpen(true);
        // Reset form
        setFormData({ name: '', email: '', phone: '', coverLetter: '' });
        setResumeFile(null);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const ext = file.name.split('.').pop()?.toLowerCase();
            if (['pdf', 'doc', 'docx'].includes(ext || '')) {
                setResumeFile(file);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File',
                    text: 'Please upload a PDF or DOCX file.',
                    confirmButtonColor: '#3b82f6',
                    customClass: { popup: 'rounded-2xl' }
                });
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !selectedJob) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Fields',
                text: 'Please fill in your name and email.',
                confirmButtonColor: '#3b82f6',
                customClass: { popup: 'rounded-2xl' }
            });
            return;
        }

        setIsSubmitting(true);
        
        const submitData = new FormData();
        submitData.append('name', formData.name);
        submitData.append('email', formData.email);
        submitData.append('phone', formData.phone);
        submitData.append('role', selectedJob.title);
        submitData.append('coverLetter', formData.coverLetter);
        if (resumeFile) {
            submitData.append('resume', resumeFile);
        }

        try {
            const response = await fetch(`${backendUrl}/api/careers/apply`, {
                method: 'POST',
                body: submitData,
            });

            const result = await response.json();
            
            if (response.ok) {
                setIsModalOpen(false);
                Swal.fire({
                    icon: 'success',
                    title: 'Application Submitted!',
                    text: 'Thank you for applying. Our team will review your application and get back to you soon.',
                    confirmButtonColor: '#3b82f6',
                    customClass: { popup: 'rounded-2xl' }
                });
            } else {
                throw new Error(result.error || 'Failed to submit application');
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: 'Submission Failed',
                text: error.message || 'There was an error submitting your application. Please try again later.',
                confirmButtonColor: '#3b82f6',
                customClass: { popup: 'rounded-2xl' }
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const departments = ['All', 'Power Platform', 'React', 'Flutter', 'AI & ML', 'Python', 'WordPress'];
    
    const filteredJobs = jobOpenings.filter(job => {
        const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                              job.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDept = filterDept === 'All' || job.department === filterDept;
        return matchesSearch && matchesDept;
    });

    return (
        <div className="min-h-screen bg-gray-50 font-sans relative overflow-hidden">
            <BackgroundDecoration />

            {/* Hero Section */}
            <section data-id="careers-hero" className="bg-gradient-to-br from-blue-900 via-blue-900 to-indigo-900 text-white pt-20 pb-10 relative z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <span className="inline-block py-1 px-3 rounded-full bg-blue-800/50 border border-blue-400 text-blue-200 text-sm font-semibold tracking-wide uppercase mb-6 animate-fade-in-up">
                            Join Our Team
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
                            Build the Future of <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 animate-gradient-x">Software</span>
                        </h1>
                        <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                            We're always looking for passionate, talented individuals to join us in creating innovative digital solutions.
                        </p>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 relative z-10">
                
                {/* Filters */}
                <div className="mb-6 flex flex-col md:flex-row gap-4 justify-between items-center bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                    <div className="relative w-full md:flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search roles or keywords..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl leading-5 bg-gray-50 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors sm:text-sm"
                        />
                    </div>
                    
                    <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto md:justify-end scrollbar-hide">
                        {departments.map(dept => (
                            <button
                                key={dept}
                                onClick={() => setFilterDept(dept)}
                                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                                    filterDept === dept 
                                    ? 'bg-gray-900 text-white shadow-md' 
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {dept}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Job Listings */}
                <div className="space-y-6">
                    {filteredJobs.length > 0 ? (
                        filteredJobs.map((job, index) => (
                            <div 
                                key={job.id} 
                                className="bg-white rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-xl border border-gray-100 transition-all duration-300 transform hover:-translate-y-1 group"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <span className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-full">
                                                {job.department}
                                            </span>
                                            <span className="flex items-center text-gray-500 text-sm">
                                                <Clock className="w-4 h-4 mr-1" /> {job.type}
                                            </span>
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                                            {job.title}
                                        </h3>
                                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                                            <span className="flex items-center"><MapPin className="w-4 h-4 mr-1.5 text-gray-400" /> {job.location}</span>
                                            <span className="flex items-center"><DollarSign className="w-4 h-4 mr-1.5 text-gray-400" /> {job.salary}</span>
                                        </div>
                                        <p className="text-gray-600 mb-6 max-w-3xl line-clamp-2 md:line-clamp-none">
                                            {job.description}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {job.tags.map(tag => (
                                                <span key={tag} className="px-3 py-1 bg-gray-50 border border-gray-200 text-gray-600 text-xs rounded-lg">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="mt-4 md:mt-0 flex-shrink-0">
                                        <button 
                                            onClick={() => openApplyModal(job)}
                                            className="w-full md:w-auto flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 md:py-3 md:text-lg md:px-6 transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        >
                                            Apply Now <ChevronRight className="ml-2 -mr-1 w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
                            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No open positions found</h3>
                            <p className="text-gray-500">We couldn't find any roles matching your current filters. Try adjusting your search.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Application Modal */}
            {isModalOpen && selectedJob && (
                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-900/60 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl transform transition-all overflow-hidden max-h-[90vh] flex flex-col">
                        
                        {/* Modal Header */}
                        <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Apply for {selectedJob.title}</h3>
                                <p className="text-sm text-gray-500 mt-1">{selectedJob.location} • {selectedJob.type}</p>
                            </div>
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                            <form id="apply-form" onSubmit={handleSubmit} className="space-y-6">
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                        <input
                                            type="text"
                                            id="name"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-gray-50 focus:bg-white"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                                        <input
                                            type="email"
                                            id="email"
                                            required
                                            value={formData.email}
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-gray-50 focus:bg-white"
                                            placeholder="john@example.com"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-gray-50 focus:bg-white"
                                        placeholder="+1 (555) 000-0000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Resume / CV *</label>
                                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-blue-400 transition-colors bg-gray-50">
                                        <div className="space-y-2 text-center">
                                            {resumeFile ? (
                                                <div className="flex flex-col items-center">
                                                    <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
                                                    <p className="text-sm font-medium text-gray-900 mt-2">{resumeFile.name}</p>
                                                    <button type="button" onClick={() => setResumeFile(null)} className="text-xs text-red-500 hover:text-red-700 mt-1">Remove file</button>
                                                </div>
                                            ) : (
                                                <>
                                                    <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
                                                    <div className="flex text-sm text-gray-600 justify-center">
                                                        <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none">
                                                            <span>Upload a file</span>
                                                            <input id="file-upload" name="file-upload" type="file" className="sr-only" accept=".pdf,.doc,.docx" onChange={handleFileChange} />
                                                        </label>
                                                        <p className="pl-1">or drag and drop</p>
                                                    </div>
                                                    <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-700 mb-1">Cover Letter (Optional)</label>
                                    <textarea
                                        id="coverLetter"
                                        rows={4}
                                        value={formData.coverLetter}
                                        onChange={(e) => setFormData({...formData, coverLetter: e.target.value})}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-gray-50 focus:bg-white resize-none"
                                        placeholder="Tell us why you're a great fit for this role..."
                                    ></textarea>
                                </div>
                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="apply-form"
                                disabled={isSubmitting}
                                className={`px-8 py-2.5 rounded-xl text-sm font-medium text-white shadow-sm transition-all flex items-center justify-center min-w-[120px] ${
                                    isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md'
                                }`}
                            >
                                {isSubmitting ? (
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                    'Submit Application'
                                )}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default Careers;
